// client/src/utils/seatDepartments.js
//
// ★ ITEM 7 — the ONE tenant-aware per-seat department resolver.
//
// A per-seat vendor scoped to specific departments needs three things, and before
// this module each of them existed as a HARDCODED SEMITEK LITERAL in FIVE places:
//   (1) the operator's option list          — VendorDashboard SEAT_DEPARTMENTS
//   (2) the same list in the bulk editor    — ForecastWizard SEAT_DEPARTMENTS
//   (3) dept → payrollHC key (FTE)          — computeVendor DEPT_TO_FTE
//   (4) dept → contractorHC bucket          — computeVendor DEPT_TO_CONTRACTOR
//   (5) a verbatim INLINE COPY of (3)+(4)   — VendorDashboard displayVendors overlay
// A department key absent from those maps was SKIPPED by a guarded lookup
// (`if (fteKey)`), so a per-seat vendor scoped to any non-Semitek department
// computed $0 — silently, forever. cid=3's eleven departments overlap Semitek's
// six by ZERO, and even the map's VALUES ("cs","content",…) do not exist in cid=3's
// payrollHC. See diffs/item7-phase0.txt for the measured trace.
//
// ★ THIS FINISHES THE KPI 6b JOB. 6b made payrollHC's KEYS tenant-derived
// (runFullModel.js:379-389: slugified department_mappings.department_name) and left
// per-seat pointing at the old literals. This module REUSES that derivation
// (`deptSlug` is exported from here and imported BY runFullModel — one function,
// not a copy) so the resolver's ids are, by construction, exactly the payrollHC
// keys. FTE resolution is therefore an IDENTITY, not a translation: the class of
// bug that produced this item cannot recur, because there is no second mapping to
// drift.
//
// ★ THE LEGACY FALLBACK IS DELIBERATE, NOT DEBT. A tenant with NO
// department_mappings rows (cid=1 Semitek / cid=2 Chris Test) keeps the hardcoded
// maps EXACTLY as they were — those two are frozen byte-identity harnesses, and
// their employees.department values ("Customer Success", "Content", …) only
// resolve through the old literals. Semitek keeps its hardcoding, cut off from the
// dynamic path rather than deleted.
//
// PURE. No React, no DB, no engine import cycles (lineRoles is a leaf constants module).

import { IS_CATEGORY_DISPLAY_TO_SLUG } from '../engines/lineRoles';

/* Department name → payrollHC key. ★ THE single slug derivation — runFullModel
   imports THIS (it previously declared an identical `_slug` inline). Changing it
   here changes payrollHC's keys and the resolver's ids together, which is the
   whole point: they can never disagree. */
export const deptSlug = s =>
  ((s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")) || "dept";

/* THE ONE RULE FOR A TEMPLATE GROUP TEXT (2026-10-28 -- SubBase, Step 5c). A GL line's display_group comes from the
   workbook's own column and may be readable text ("Cost of Revenue", "Sales"); a department's group id is ALWAYS a slug
   (Step 5c: deptSlug of the Group Name, or of the department name). Wherever the two meet -- a payroll IS-line key, an
   income-statement sub-group, a sort band, the Step-3 sub-group picker -- the text means the department group whose id is
   deptSlug(text). deptSlug(slug) === slug, so a group already stored as a slug (every company before SubBase) is unchanged.
   A BLANK (null / undefined / '') is returned AS IS: a blank means "no sub-group", never the group 'dept'.
   Stored data is never rewritten -- every reader compares through this. (diffs/step5-group-slug-fix-build.txt) */
export const groupIdOf = g => (g == null || g === "") ? g : deptSlug(g);

/* Heading text for a group the template names but NO department owns (SubBase's "Marketing"): the stored template text,
   never a title-cased slug. Returns { groupId: text } ONLY where the text is not already its own id (so a company whose
   groups are stored as slugs gets {} -- its headings are unchanged) and the id is not a department's group (a department's
   group keeps its Group Name, or today's label). Two texts that slug alike resolve to the alphabetically first, whatever
   order the rows arrive in. */
export function templateGroupLabels(groupTexts, deptMappings) {
  const deptIds = new Set((deptMappings || []).map(d => d.display_group || deptSlug(d.department_name)));
  const out = {};
  for (const t of [...new Set((groupTexts || []).filter(x => x != null && x !== ""))].sort()) {
    const id = deptSlug(t);
    if (id === t || deptIds.has(id) || out[id] != null) continue;
    out[id] = t;
  }
  return out;
}

/* IS-category slug → contractor headcount bucket. The ONE place that decision is
   made: computeVendor's `catToBucket` (which classifies an is_line PREFIX) now
   delegates here, so the vendor-engine's bucketing and the per-seat department
   bucketing cannot diverge. Buckets are canonical across tenants — unlike the FTE
   keys, they were never Semitek-shaped. */
export const CONTRACTOR_BUCKET_BY_CAT_SLUG = {
  cogs: "contractors_cogs",
  sm: "contractors_sm",
  ga: "contractors_ga",
  rd: "contractors_rd",
};
export function contractorBucketForCatSlug(catSlug) {
  return CONTRACTOR_BUCKET_BY_CAT_SLUG[catSlug] || null;
}

/* ⛔ LEGACY_SEAT_DEPARTMENTS / LEGACY_DEPT_TO_FTE / LEGACY_DEPT_TO_CONTRACTOR DELETED HERE
   (P4-a). Semitek's six per-seat departments and their FTE / contractor buckets. They
   existed so cid=1 / cid=2 kept computing what they always had; those tenants were deleted
   2026-08-12. Their ONLY consumer was the `useLegacyFallback === true` arm of
   buildSeatDeptResolver below — measured: one code reference each, all inside that arm. */

/**
 * buildSeatDeptResolver(deptMappings) → the tenant's per-seat department contract.
 *
 * @param {Array|null} deptMappings  rows of department_mappings
 *        ({ department_name, is_category, … }); empty/absent ⇒ LEGACY branch.
 * @returns {{
 *   isLegacy: boolean,
 *   options: Array<{id:string,label:string}>,   // what the operator may choose
 *   fteKey: (id:string) => string|null,         // → payrollHC[key]
 *   contractorBucket: (id:string) => string|null // → contractorHC[bucket]
 * }}
 *
 * Both resolvers return null for an unknown id — the caller must skip it (the
 * historical behaviour). Returning null rather than a default is deliberate: a
 * wrong department is a $0 either way, and inventing one would book a real cost
 * against a department the operator never chose.
 */
export function buildSeatDeptResolver(deptMappings) {
  const rows = deptMappings || [];

  /* ★★ P4-a — THE LEGACY ARM IS DELETED, AND THIS ONE WAS *NOT* SIMPLY UNREACHABLE.
   * Every other arm in this teardown required a company flag that is false everywhere. This
   * one was ALSO entered from `computeVendor.js`'s module-scope
   * `buildSeatDeptResolver([], true)` — a HARDCODED true, evaluated on every page load,
   * independent of any tenant. What made it inert was not that it never ran, but that its
   * RESULT was never consumed: `computeVendorIS` has exactly one call site
   * (runFullModel:540) which always passes a real resolver, and all three returns here are
   * truthy objects, so the `|| LEGACY_SEAT_RESOLVER` fallback could never be taken.
   * ⇒ That call site and its `||` were deleted in the SAME commit. Deleting this arm alone
   *   would have left a module-load call whose meaning had silently changed — which is
   *   exactly what computeVendor's own comment warned about.
   * ⓘ P4-b removed the `useLegacyFallback` parameter. */

  // ★ FAIL LOUD: flag false AND no mappings. NO options to choose from and every
  // lookup returns null, so a per-seat vendor computes $0 — the SAME thing an unknown
  // id has always done, but now honestly, instead of billing against another
  // company's departments. `unmappedTenant` lets a caller name the reason.
  if (!rows.length) {
    return {
      isLegacy: false,
      unmappedTenant: true,
      options: [],
      fteKey: () => null,
      contractorBucket: () => null,
    };
  }

  const options = [];
  const seen = new Set();
  const bucketById = {};
  rows.forEach(r => {
    const id = deptSlug(r.department_name);
    if (!seen.has(id)) {
      seen.add(id);
      options.push({ id, label: r.department_name });
    }
    // First non-null category wins if two departments slug identically (they do not
    // on any live tenant; this only keeps the map deterministic).
    if (bucketById[id] == null) {
      bucketById[id] = contractorBucketForCatSlug(IS_CATEGORY_DISPLAY_TO_SLUG[r.is_category]);
    }
  });

  return {
    isLegacy: false,
    options,
    // ★ IDENTITY, not translation: payrollHC is keyed by exactly these slugs
    // (runFullModel.js builds it with the same `deptSlug`). The guard keeps an
    // unknown/stale stored id from silently reading an unrelated key.
    fteKey: id => (seen.has(id) ? id : null),
    contractorBucket: id => bucketById[id] || null,
  };
}
